import { makeAutoObservable } from 'mobx';

import { ApiRoutes } from '../../config/ApiRoutes';
import type { RootStore } from '..';

export interface MotorClubLinkedUnit {
  status: string;
  unitNumber: string;
  vinNumber: string;
  cancelledDate: string;
  id: string;
  unitEnrolled: number;
  unitVacant: number;
  unitPurchased: number;
}

export interface MotorClubMembershipDriver {
  driverLicense: string;
  driverLicenseType: string;
  driverId: string;
  driverName: string;
  enrolledDate: string;
  cancelledDate: string;
  linkedUnit: MotorClubLinkedUnit[];
  memberId: string;
  status: string;
  id: string;
  driverEnrolled: number;
  driverVacant: number;
  driverPurchased: number;
}

export interface MotorClubMembershipsResponse {
  items: MotorClubMembershipDriver[];
  nextLink?: string;
  total: number;
}

export interface MotorClubRoadsideUnitDriver {
  driverLicense: string;
  driverLicenseType: string;
  driverId: string;
  driverName: string;
  enrolledDate: string;
  cancelledDate: string;
  memberId: string;
  status: string;
}

export interface MotorClubRoadsideUnit {
  driverMembership: MotorClubRoadsideUnitDriver | null;
  memberId: string;
  status: string;
  unitNumber: string;
  vinNumber: string;
  cancelledDate: string;
  id: string;
  unitEnrolled: number;
  unitVacant: number;
  unitPurchased: number;
}

export interface MotorClubRoadsideUnitsResponse {
  items: MotorClubRoadsideUnit[];
  nextLink?: string;
  total: number;
  hasMore: boolean;
}

export interface MotorClubEligibleUnit {
  licensePlate: string;
  make: string;
  model: string;
  ownershipType: string;
  primaryDriver: string;
  routeType: string;
  unitId: string;
  unitNumber: string;
  unitType: string;
  vinNumber: string;
  year: string;
}

export interface MotorClubEligibleUnitsResponse {
  items: MotorClubEligibleUnit[];
  total: number;
}

export interface MotorClubStateOption {
  code: string;
  fullName: string;
  id: string;
  index: number;
}

export interface MotorClubEligibleDriver {
  driverId: string;
  driverName: string;
  licenseNumber: string;
  licenseType: string;
}

export interface MotorClubEligibleDriversResponse {
  items: MotorClubEligibleDriver[];
  total: number;
}

export interface MotorClubTrafficIncidentDriverLookupItem {
  driverId: string;
  driverName: string;
}

export interface MotorClubTrafficIncidentDriversLookupResponse {
  items: MotorClubTrafficIncidentDriverLookupItem[];
  total: number;
}

export interface AddMotorClubTrafficIncidentRequest {
  ackAttorney: boolean;
  ackMvr: boolean;
  driverId: string;
  incidentDate: string;
  stateIssued: string;
  statement: string;
}

export interface AddMotorClubTrafficIncidentResponse {
  incidentId: string;
  incidentNumber: string;
}

export interface TerminateMotorClubMembershipRequest {
  id: string;
  membershipType: number;
}

export interface TerminateMotorClubMembershipResponse {
  id: string;
  membershipType: number;
  notificationSent: boolean;
  notifiedDriverIds: string[];
  statusUpdatedToTerminated: boolean;
  terminatedAt: string;
}

export interface MotorClubTrafficIncident {
  incidentId: string;
  dateIssued: string;
  dateSubmitted: string;
  driver: string;
  driverId: string;
  incidentType: string;
  number: string;
  state: string;
  status: string;
}

export interface MotorClubTrafficIncidentsResponse {
  items: MotorClubTrafficIncident[];
  nextLink?: string;
  total: number;
}

export interface MotorClubTrafficIncidentDetails {
  attorneyAcknowledged: boolean;
  dateIssued: string;
  dateSubmitted: string;
  driver: string;
  incidentId: string;
  mvrAcknowledged: boolean;
  number: string;
  state: string;
  statement: string;
  status: string;
}

export interface MotorClubMembershipDetailsResponse {
  memberId?: string;
  activeMembers: number;
  activeUnits: number;
  vacantMembers: number;
  vacantUnits: number;
  programType: string;
  currentTermEnd: string;
  currentTermStart: string;
  status: string;
  subscriptionId: string;
  totalPurchasedMembers: number;
  totalPurchasedUnits: number;
}

export interface AddMotorClubUnitPayload {
  primaryDriverId: string;
  unitNumber: string;
  unitType: string;
  vehicleMake?: string;
  vehicleModel?: string;
  vehicleYear?: string;
  vinNumber: string;
}

export interface AddMotorClubUnitMembershipRequest {
  existingUnitId?: string;
  linkedDriverId?: string;
  linkedDriverMembershipId?: string;
  unit?: AddMotorClubUnitPayload;
}

export interface AddMotorClubUnitMembershipResponse {
  accountServiceId: string;
  driverMembershipId: string;
  membershipId: string;
  type: number;
  unitId: string;
}

export interface AddMotorClubDriverPayload {
  email?: string;
  firstName: string;
  lastName: string;
  licenseStateId?: string;
  phoneNumber?: string;
}

export interface AddMotorClubDriverUnitPayload {
  unitNumber: string;
  unitType?: string;
  vehicleMake?: string;
  vehicleModel?: string;
  vehicleYear?: string;
  vinNumber: string;
}

export interface AddMotorClubDriverMembershipRequest {
  driverId?: string;
  driver?: AddMotorClubDriverPayload;
  unit?: AddMotorClubDriverUnitPayload;
}

export interface AddMotorClubDriverMembershipResponse {
  accountServiceId: string;
  driverId: string;
  driverMembershipId: string;
  unitId?: string;
  unitMembershipId?: string;
}

export interface MotorClubActiveDriverMembershipResponse {
  hasActiveDriverMembership: boolean;
}

export class MotorClubMembershipApiStore {
  constructor(public rootStore: RootStore) {
    makeAutoObservable(this, { rootStore: false });
  }

  getMembershipDetails = () => {
    const {
      Details: { Endpoint, Method },
    } = ApiRoutes.MotorClubs;

    return this.rootStore.apiStore.call<MotorClubMembershipDetailsResponse>({
      method: Method,
      url: Endpoint,
    });
  };

  getDriverMembershipDetails = () => {
    const {
      DriverMembershipDetails: { Endpoint, Method },
    } = ApiRoutes.MotorClubs;

    return this.rootStore.apiStore.call<MotorClubMembershipDetailsResponse>({
      method: Method,
      url: Endpoint,
    });
  };

  hasActiveDriverMembership = () => {
    const {
      ActiveDriverMembership: { Endpoint, Method },
    } = ApiRoutes.MotorClubs;

    return this.rootStore.apiStore.call<MotorClubActiveDriverMembershipResponse>(
      {
        method: Method,
        url: Endpoint,
      },
    );
  };

  getMemberships = (params?: {
    limit?: number;
    nextLink?: string;
    terminated?: boolean;
  }) => {
    const {
      Memberships: { Endpoint, Method },
    } = ApiRoutes.MotorClubs;

    return this.rootStore.apiStore.call<MotorClubMembershipsResponse>({
      method: Method,
      params,
      url: Endpoint,
    });
  };

  getRoadsideUnits = (params?: {
    limit?: number;
    nextLink?: string;
    terminated?: boolean;
  }) => {
    const {
      RoadsideUnits: { Endpoint, Method },
    } = ApiRoutes.MotorClubs;

    return this.rootStore.apiStore.call<MotorClubRoadsideUnitsResponse>({
      method: Method,
      params,
      url: Endpoint,
    });
  };

  getEligibleUnits = () => {
    const {
      EligibleUnits: { Endpoint, Method },
    } = ApiRoutes.MotorClubs;

    return this.rootStore.apiStore.call<MotorClubEligibleUnitsResponse>({
      method: Method,
      url: Endpoint,
    });
  };

  getEligibleDrivers = (onlyActive?: boolean) => {
    const {
      EligibleDrivers: { Endpoint, Method },
    } = ApiRoutes.MotorClubs;

    return this.rootStore.apiStore.call<MotorClubEligibleDriversResponse>({
      method: Method,
      params: { onlyActive },
      url: Endpoint,
    });
  };

  getMotorClubStates = () => {
    const {
      States: { Endpoint, Method },
    } = ApiRoutes.MotorClubs;

    return this.rootStore.apiStore.call<MotorClubStateOption[]>({
      method: Method,
      url: Endpoint,
    });
  };

  getTrafficIncidents = (params?: { limit?: number; nextLink?: string }) => {
    const {
      TrafficIncidents: { Endpoint, Method },
    } = ApiRoutes.MotorClubs;

    return this.rootStore.apiStore.call<MotorClubTrafficIncidentsResponse>({
      method: Method,
      params,
      url: Endpoint,
    });
  };

  getTrafficIncidentDriversLookup = () => {
    const {
      TrafficIncidentDriversLookup: { Endpoint, Method },
    } = ApiRoutes.MotorClubs;

    return this.rootStore.apiStore.call<MotorClubTrafficIncidentDriversLookupResponse>(
      {
        method: Method,
        url: Endpoint,
      },
    );
  };

  getTrafficIncidentDetails = (incidentId: string) => {
    const {
      TrafficIncidentDetails: { Endpoint, Method },
    } = ApiRoutes.MotorClubs;

    return this.rootStore.apiStore.call<MotorClubTrafficIncidentDetails>({
      method: Method,
      url: Endpoint(incidentId),
    });
  };

  getTrafficIncidentSubIncidents = (incidentId: string) => {
    const {
      TrafficIncidentSubIncidents: { Endpoint, Method },
    } = ApiRoutes.MotorClubs;

    return this.rootStore.apiStore.call<MotorClubTrafficIncidentsResponse>({
      method: Method,
      url: Endpoint(incidentId),
    });
  };

  addTrafficIncident = (data: AddMotorClubTrafficIncidentRequest) => {
    const {
      AddTrafficIncident: { Endpoint, Method },
    } = ApiRoutes.MotorClubs;

    return this.rootStore.apiStore.call<AddMotorClubTrafficIncidentResponse>({
      data,
      method: Method,
      url: Endpoint,
    });
  };

  addUnitMembership = (data: AddMotorClubUnitMembershipRequest) => {
    const {
      AddUnitMembership: { Endpoint, Method },
    } = ApiRoutes.MotorClubs;

    return this.rootStore.apiStore.call<AddMotorClubUnitMembershipResponse>({
      data,
      method: Method,
      url: Endpoint,
    });
  };

  addDriverMembership = (data: AddMotorClubDriverMembershipRequest) => {
    const {
      AddDriverMembership: { Endpoint, Method },
    } = ApiRoutes.MotorClubs;

    return this.rootStore.apiStore.call<AddMotorClubDriverMembershipResponse>({
      data,
      method: Method,
      url: Endpoint,
    });
  };

  terminateMembership = (data: TerminateMotorClubMembershipRequest) => {
    const {
      TerminateMembership: { Endpoint, Method },
    } = ApiRoutes.MotorClubs;

    return this.rootStore.apiStore.call<TerminateMotorClubMembershipResponse>({
      data,
      method: Method,
      url: Endpoint,
    });
  };
}
